import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Stack, Typography, Chip, Alert, AlertTitle, CircularProgress, IconButton, Button, Paper,
  Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Tooltip, Snackbar, Switch, FormControlLabel, LinearProgress, Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { LineChart } from "@mui/x-charts/LineChart";
import { BRAND } from "../styles/theme";
import { fmtArDateTime } from "../services/monitoring";
import {
  listDominios, getDominio, getHistorico, reevaluar,
  CLASS_META, WHERE_LABEL, MANAGED_IN_LABEL, SEVERITY_ORDER,
  type DominioSummary, type DominioStatus, type HistoricoPoint, type Finding, type Source,
  type Severity, type SourceClass, type FindingStatus, type Dmarc, type Dns, type Traffic,
  type OpenBySeverity,
} from "../services/dominio";

// Tiempo que tarda el evaluator en volver a escribir status#latest tras "Reevaluar".
const REEVAL_REFRESH_MS = 20_000;

const nf = new Intl.NumberFormat("es-AR");

// ---------- Metadatos de color ----------
const SEV_META: Record<Severity, { fg: string; bg: string; label: string }> = {
  critical: { fg: "#7F1D1D", bg: "#FECACA", label: "CRÍTICO" },
  high:     { fg: "#B91C1C", bg: "#FEE2E2", label: "ALTO" },
  medium:   { fg: "#9A3412", bg: "#FFEDD5", label: "MEDIO" },
  low:      { fg: "#1D4ED8", bg: "#DBEAFE", label: "BAJO" },
  info:     { fg: "#374151", bg: "#F1F5F9", label: "INFO" },
};

const STATUS_LABEL: Record<FindingStatus, string> = {
  open: "abierto", accepted: "aceptado", deferred: "diferido", out_of_scope: "fuera de alcance",
};

const CLASS_BAR_COLOR: Record<SourceClass, string> = {
  ok: "#16A34A", parcial: "#EAB308", autorizado_sin_alinear: "#F97316", google_sin_spf: "#3B82F6", no_autorizado: "#DC2626",
};
const CLASS_ORDER: SourceClass[] = ["ok", "parcial", "autorizado_sin_alinear", "google_sin_spf", "no_autorizado"];
const SEVERITIES: Severity[] = ["critical", "high", "medium", "low", "info"];

// ---------- Helpers de formato ----------
function fmtPct(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${Number(v).toFixed(v >= 99.95 || Number.isInteger(v) ? 0 : 1)} %`;
}

/** "2026-09-17" → "17/09/2026" (la fecha ya es de negocio, sin tz). */
function fmtDay(s: string | null | undefined): string {
  if (!s) return "—";
  const [y, m, d] = s.split("-");
  return d && m && y ? `${d}/${m}/${y}` : s;
}

function fmtWhen(iso: string | null | undefined): string {
  return iso ? fmtArDateTime(iso) : "—";
}

function openCount(o: OpenBySeverity | undefined): number {
  return Object.values(o ?? {}).reduce((a, b) => a + (b ?? 0), 0);
}

// ---------- Chips ----------
function PolicyChip({ dmarc, size = "small" }: { dmarc?: Pick<Dmarc, "present" | "p"> | null; size?: "small" | "medium" }) {
  let fg = "#B91C1C", bg = "#FEE2E2", label = "sin DMARC", tip = "El dominio no publica registro DMARC: cualquiera puede mandar en su nombre sin que los receptores lo marquen.";
  if (dmarc?.present && dmarc.p === "none")       { fg = "#9A3412"; bg = "#FFEDD5"; label = "p=none";       tip = "DMARC en modo monitoreo: se reciben reportes pero no se frena nada."; }
  if (dmarc?.present && dmarc.p === "quarantine") { fg = "#854D0E"; bg = "#FEF9C3"; label = "p=quarantine"; tip = "Los mails que fallan DMARC van a spam."; }
  if (dmarc?.present && dmarc.p === "reject")     { fg = "#166534"; bg = "#DCFCE7"; label = "p=reject";     tip = "Los mails que fallan DMARC se rechazan."; }
  if (dmarc?.present && !dmarc.p)                 { fg = "#B91C1C"; bg = "#FEE2E2"; label = "DMARC sin p="; tip = "Hay registro DMARC pero sin política válida."; }
  return (
    <Tooltip title={tip}>
      <Chip size={size} label={label} sx={{ fontWeight: 800, fontFamily: "monospace", color: fg, bgcolor: bg, height: size === "small" ? 22 : undefined }} />
    </Tooltip>
  );
}

function SevChip({ severity }: { severity: Severity }) {
  const m = SEV_META[severity] ?? SEV_META.info;
  return <Chip size="small" label={m.label} sx={{ fontWeight: 800, fontSize: 11, height: 22, color: m.fg, bgcolor: m.bg }} />;
}

function ClassChip({ cls }: { cls: SourceClass }) {
  const m = CLASS_META[cls] ?? CLASS_META.parcial;
  return (
    <Tooltip title={m.tip}>
      <Chip size="small" label={m.label} sx={{ fontWeight: 700, fontSize: 11, height: 22, color: m.fg, bgcolor: m.bg }} />
    </Tooltip>
  );
}

function StatusChip({ status }: { status: FindingStatus }) {
  return <Chip size="small" variant="outlined" label={STATUS_LABEL[status] ?? status} sx={{ fontSize: 11, height: 22, color: "#374151" }} />;
}

function OpenBySeverityChips({ o, emptyText = "sin hallazgos abiertos" }: { o?: OpenBySeverity; emptyText?: string }) {
  const parts = SEVERITIES.filter((s) => (o?.[s] ?? 0) > 0);
  if (parts.length === 0) return <Typography variant="caption" sx={{ color: "#166534", fontWeight: 700 }}>{emptyText}</Typography>;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {parts.map((s) => (
        <Chip key={s} size="small" label={`${o![s]} ${SEV_META[s].label.toLowerCase()}`}
          sx={{ fontWeight: 700, fontSize: 11, height: 20, color: SEV_META[s].fg, bgcolor: SEV_META[s].bg }} />
      ))}
    </Stack>
  );
}

function YesNo({ v }: { v: boolean | null | undefined }) {
  if (v == null) return <span style={{ color: "#6B7280" }}>—</span>;
  return v
    ? <CheckIcon fontSize="inherit" sx={{ color: "#166534", verticalAlign: "middle" }} />
    : <CloseIcon fontSize="inherit" sx={{ color: "#B91C1C", verticalAlign: "middle" }} />;
}

// ---------- Piezas ----------
function StatTile({ label, value, color, hint }: { label: string; value: string; color?: string; hint?: string }) {
  return (
    <Box sx={{ minWidth: 130, p: 1.5, borderRadius: 2, border: "1px solid rgba(0,0,0,0.08)" }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.15, color: color ?? "#222" }}>{value}</Typography>
      {hint && <Typography variant="caption" sx={{ color: "text.secondary" }}>{hint}</Typography>}
    </Box>
  );
}

function SectionCard({ title, children, sx }: { title: string; children: React.ReactNode; sx?: object }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 260, ...sx }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>{title}</Typography>
      {children}
    </Paper>
  );
}

function KV({ k, v, mono }: { k: string; v: React.ReactNode; mono?: boolean }) {
  return (
    <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ py: 0.4, borderBottom: "1px dashed rgba(0,0,0,0.06)" }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>{k}</Typography>
      <Typography variant="body2" component="div" sx={{ fontWeight: 600, textAlign: "right", fontFamily: mono ? "monospace" : undefined, wordBreak: "break-all" }}>{v}</Typography>
    </Stack>
  );
}

function RecordBox({ label, value, onCopy, emptyText = "sin registro" }: { label?: string; value: string | null | undefined; onCopy: (t: string) => void; emptyText?: string }) {
  return (
    <Box sx={{ position: "relative", bgcolor: "#F8FAFC", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 1.5, p: 1.25, pr: 5, mb: 1 }}>
      {label && <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, display: "block" }}>{label}</Typography>}
      <Typography component="pre" sx={{ m: 0, fontFamily: "monospace", fontSize: 12.5, whiteSpace: "pre-wrap", wordBreak: "break-all", color: value ? "#111" : "#9CA3AF" }}>
        {value || emptyText}
      </Typography>
      {value && (
        <Tooltip title="Copiar">
          <IconButton size="small" onClick={() => onCopy(value)} sx={{ position: "absolute", top: 4, right: 4 }}>
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

/** Barra apilada de `by_class` con leyenda. */
function ClassBar({ traffic }: { traffic: Pick<Traffic, "by_class" | "total"> }) {
  const total = traffic.total || 0;
  if (total === 0) return <Typography variant="body2" sx={{ color: "text.secondary" }}>Sin tráfico en la ventana.</Typography>;
  return (
    <Box>
      <Box sx={{ display: "flex", height: 14, borderRadius: 7, overflow: "hidden", border: "1px solid rgba(0,0,0,0.08)" }}>
        {CLASS_ORDER.map((c) => {
          const n = traffic.by_class?.[c] ?? 0;
          if (!n) return null;
          return (
            <Tooltip key={c} title={`${CLASS_META[c].label}: ${nf.format(n)} (${fmtPct(n * 100 / total)})`}>
              <Box sx={{ width: `${(n * 100) / total}%`, bgcolor: CLASS_BAR_COLOR[c], minWidth: n ? 3 : 0 }} />
            </Tooltip>
          );
        })}
      </Box>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        {CLASS_ORDER.map((c) => (
          <Stack key={c} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: 5, bgcolor: CLASS_BAR_COLOR[c] }} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {CLASS_META[c].label} <b>{nf.format(traffic.by_class?.[c] ?? 0)}</b>
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

// ---------- Tarjeta por dominio ----------
function DomainCard({ s, selected, onClick }: { s: DominioSummary; selected: boolean; onClick: () => void }) {
  const tr = s.traffic;
  return (
    <Paper variant="outlined" onClick={onClick}
      sx={{ p: 1.75, cursor: "pointer", flex: "1 1 220px", minWidth: 220, maxWidth: 320,
            borderColor: selected ? BRAND.primary : undefined, borderWidth: selected ? 2 : 1,
            bgcolor: selected ? BRAND.primaryLight : undefined, transition: "border-color .15s",
            "&:hover": { borderColor: BRAND.primary } }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 16, flex: 1 }}>{s.domain}</Typography>
        {s.ticket && <Chip size="small" label={s.ticket} variant="outlined" sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />}
      </Stack>
      {s.missing ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>Todavía no evaluado. Usá “Reevaluar ahora”.</Typography>
      ) : (
        <>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <PolicyChip dmarc={s.dns?.dmarc} />
            {s.recommendation?.ready_for && (
              <Chip size="small" label={`Listo para ${s.recommendation.ready_for}`} sx={{ height: 22, fontWeight: 800, fontSize: 11, color: "#166534", bgcolor: "#DCFCE7" }} />
            )}
          </Stack>
          <Box sx={{ mb: 1 }}><OpenBySeverityChips o={s.open_by_severity} /></Box>
          <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>% alineado</Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.1 }}>{fmtPct(tr?.aligned_pct)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>mails {tr?.window_days ?? 30} d</Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.1 }}>{nf.format(tr?.total ?? 0)}</Typography>
            </Box>
          </Stack>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {s.recommendation?.next_step ?? "—"}
          </Typography>
        </>
      )}
    </Paper>
  );
}

// ---------- Tab: Resumen ----------
function ResumenTab({ st }: { st: DominioStatus }) {
  const rec = st.recommendation;
  const tr = st.traffic;
  return (
    <Stack spacing={2}>
      <Alert severity={rec.ready_for ? "success" : "info"} icon={false}>
        <AlertTitle sx={{ fontWeight: 800 }}>{rec.ready_for ? `Listo para pasar a p=${rec.ready_for}` : "Próximo paso"}</AlertTitle>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{rec.next_step}</Typography>
        {rec.reasons?.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 0.75, pl: 2.5 }}>
            {rec.reasons.map((r, i) => <li key={i}><Typography variant="body2">{r}</Typography></li>)}
          </Box>
        )}
      </Alert>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="stretch">
        <SectionCard title="Uso">
          <KV k="Recibe mail (tiene MX)" v={<YesNo v={st.usage?.receives_mail} />} />
          <KV k="Envía mail (observado en reportes)" v={<YesNo v={st.usage?.sends_mail_observed} />} />
          <KV k="Remitentes declarados en el SPF" v={<YesNo v={st.usage?.senders_in_spf} />} />
          <KV k="DNS" v={MANAGED_IN_LABEL[st.managed_in] ?? st.managed_in} />
          <KV k="Ticket" v={st.ticket ?? "—"} />
          <KV k="Evaluado" v={fmtWhen(st.evaluated_at)} />
        </SectionCard>

        <SectionCard title="Reportes DMARC recibidos">
          <Stack direction="row" spacing={1.5} sx={{ mb: 1 }}>
            <StatTile label="Total" value={nf.format(st.reports?.count_total ?? 0)} />
            <StatTile label="Últimos 7 días" value={nf.format(st.reports?.count_7d ?? 0)} />
          </Stack>
          <KV k="Último recibido" v={fmtWhen(st.reports?.last_received_at)} />
          <KV k="Primero / último período" v={`${fmtWhen(st.reports?.first_at)} → ${fmtWhen(st.reports?.last_at)}`} />
          {st.reports?.orgs?.length > 0 && (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead><TableRow><TableCell>Receptor (org)</TableCell><TableCell align="right">Reportes</TableCell></TableRow></TableHead>
              <TableBody>
                {st.reports.orgs.map((o) => (
                  <TableRow key={o.name}><TableCell sx={{ fontSize: 12 }}>{o.name}</TableCell><TableCell align="right" sx={{ fontSize: 12, fontWeight: 700 }}>{nf.format(o.count)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </SectionCard>

        <SectionCard title={`Tráfico ${tr?.window_days ?? 30} días (desde ${fmtDay(tr?.since)})`} sx={{ minWidth: 320 }}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <StatTile label="Mails" value={nf.format(tr?.total ?? 0)} hint={`${tr?.days_with_data ?? 0} días con datos`} />
            <StatTile label="% alineado" value={fmtPct(tr?.aligned_pct)} color={tr?.aligned_pct != null && tr.aligned_pct < 98 ? "#B45309" : "#166534"}
              hint={`${nf.format(tr?.aligned ?? 0)} ok · ${nf.format(tr?.unaligned ?? 0)} no`} />
            <StatTile label="No autorizados" value={nf.format(tr?.unauthorized ?? 0)} color={tr?.unauthorized ? "#B91C1C" : "#166534"} />
          </Stack>
          <ClassBar traffic={tr} />
          <Alert severity={tr?.unauthorized ? "warning" : "success"} sx={{ mt: 1.5, py: 0 }}>
            <b>{nf.format(tr?.unauthorized ?? 0)}</b> mails de IPs no autorizadas: esto es lo que <b>quarantine/reject</b> frenaría.
          </Alert>
        </SectionCard>
      </Stack>
    </Stack>
  );
}

// ---------- Tab: Hallazgos ----------
function HallazgosTab({ findings }: { findings: Finding[] }) {
  const [soloAbiertos, setSoloAbiertos] = useState(true);
  const rows = useMemo(() => {
    const list = soloAbiertos ? findings.filter((f) => f.status === "open") : findings;
    return [...list].sort((a, b) => (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9));
  }, [findings, soloAbiertos]);
  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, flex: 1 }}>
          Hallazgos ({rows.length}{soloAbiertos ? ` abiertos de ${findings.length}` : ""})
        </Typography>
        <FormControlLabel control={<Switch size="small" checked={soloAbiertos} onChange={(e) => setSoloAbiertos(e.target.checked)} />}
          label={<Typography variant="body2">sólo abiertos</Typography>} />
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Severidad</TableCell><TableCell>Hallazgo</TableCell><TableCell>Detalle</TableCell>
            <TableCell>Acción</TableCell><TableCell>Dónde</TableCell><TableCell>Estado</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={6} align="center" sx={{ py: 2, color: "text.secondary" }}>
              {soloAbiertos ? "Sin hallazgos abiertos." : "Sin hallazgos."}
            </TableCell></TableRow>
          )}
          {rows.map((f, i) => (
            <TableRow key={`${f.code}-${i}`} hover sx={{ opacity: f.status === "open" ? 1 : 0.7 }}>
              <TableCell sx={{ whiteSpace: "nowrap" }}><SevChip severity={f.severity} /></TableCell>
              <TableCell sx={{ fontSize: 13, fontWeight: 700, minWidth: 180 }}>
                {f.title}
                <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontFamily: "monospace" }}>{f.code}</Typography>
              </TableCell>
              <TableCell sx={{ fontSize: 12, maxWidth: 380 }}>{f.detail}</TableCell>
              <TableCell sx={{ fontSize: 12, maxWidth: 380 }}>{f.action}</TableCell>
              <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{WHERE_LABEL[f.where] ?? f.where}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}><StatusChip status={f.status} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

// ---------- Tab: Fuentes ----------
type SrcKey = "count" | "aligned_pct" | "last_seen";

function FuentesTab({ traffic }: { traffic: Traffic }) {
  const [orderBy, setOrderBy] = useState<SrcKey>("count");
  const [dir, setDir] = useState<"asc" | "desc">("desc");
  const sources = useMemo(() => {
    const list = [...(traffic.sources ?? [])];
    const sign = dir === "asc" ? 1 : -1;
    list.sort((a: Source, b: Source) => {
      const av = a[orderBy] ?? -1, bv = b[orderBy] ?? -1;
      return (av < bv ? -1 : av > bv ? 1 : 0) * sign;
    });
    return list;
  }, [traffic.sources, orderBy, dir]);
  const onSort = (k: SrcKey) => {
    if (orderBy === k) setDir(dir === "asc" ? "desc" : "asc");
    else { setOrderBy(k); setDir("desc"); }
  };
  const total = traffic.total || 0;
  const daysOk = (traffic.days_with_data ?? 0) >= 14;

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Fuentes por IP ({sources.length})</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Una fila por IP que mandó mails con el dominio en el From, según los reportes agregados de los últimos {traffic.window_days} días.
          </Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>IP</TableCell><TableCell>PTR</TableCell><TableCell>Clase</TableCell>
                <TableCell align="right" sortDirection={orderBy === "count" ? dir : false}>
                  <TableSortLabel active={orderBy === "count"} direction={orderBy === "count" ? dir : "desc"} onClick={() => onSort("count")}>Mails</TableSortLabel>
                </TableCell>
                <TableCell align="right" sortDirection={orderBy === "aligned_pct" ? dir : false}>
                  <TableSortLabel active={orderBy === "aligned_pct"} direction={orderBy === "aligned_pct" ? dir : "desc"} onClick={() => onSort("aligned_pct")}>% alineado</TableSortLabel>
                </TableCell>
                <TableCell align="right">DKIM pass</TableCell><TableCell align="right">SPF pass</TableCell>
                <TableCell>Vía (SPF)</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Primera</TableCell>
                <TableCell sortDirection={orderBy === "last_seen" ? dir : false} sx={{ whiteSpace: "nowrap" }}>
                  <TableSortLabel active={orderBy === "last_seen"} direction={orderBy === "last_seen" ? dir : "desc"} onClick={() => onSort("last_seen")}>Última</TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sources.length === 0 && (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 2, color: "text.secondary" }}>Sin fuentes en la ventana.</TableCell></TableRow>
              )}
              {sources.map((s) => (
                <TableRow key={s.ip} hover sx={{ bgcolor: s.class === "no_autorizado" ? "#FEF2F2" : undefined }}>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12.5, fontWeight: 700, whiteSpace: "nowrap" }}>
                    {s.ip}
                    {s.new && <Chip size="small" label="nueva" sx={{ ml: 0.5, height: 18, fontSize: 10, fontWeight: 800, color: "#5B2FEB", bgcolor: "#EEE7FF" }} />}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <Tooltip title={[s.ptr ?? "", ...(s.reporters?.length ? [`Reportado por: ${s.reporters.join(", ")}`] : [])].filter(Boolean).join(" · ")}>
                      <span>{s.ptr ?? "—"}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell><ClassChip cls={s.class} /></TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700 }}>
                    {nf.format(s.count)}
                    <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>{total ? fmtPct(s.count * 100 / total) : ""}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: s.aligned_pct != null && s.aligned_pct < 100 ? "#B45309" : "#166534" }}>{fmtPct(s.aligned_pct)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{nf.format(s.dkim_pass)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12 }}>{nf.format(s.spf_pass)}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>
                    {s.via?.length ? s.via.join(", ") : (s.in_spf ? "directa" : <span style={{ color: "#B91C1C" }}>no está en el SPF</span>)}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{fmtDay(s.first_seen)}</TableCell>
                  <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>{fmtDay(s.last_seen)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper variant="outlined" sx={{ overflow: "hidden", maxWidth: 560 }}>
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Tráfico por proveedor del SPF</Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>Mails alineados que salieron por cada <code>include:</code> en {traffic.window_days} días.</Typography>
        </Box>
        <Table size="small">
          <TableHead><TableRow><TableCell>include</TableCell><TableCell align="right">Mails {traffic.window_days} d</TableCell></TableRow></TableHead>
          <TableBody>
            {(traffic.includes_usage ?? []).length === 0 && (
              <TableRow><TableCell colSpan={2} align="center" sx={{ py: 2, color: "text.secondary" }}>El SPF no tiene includes.</TableCell></TableRow>
            )}
            {(traffic.includes_usage ?? []).map((u) => {
              const unused = u.count === 0 && daysOk;
              const row = (
                <TableRow key={u.include} hover sx={{ opacity: unused ? 0.55 : 1 }}>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12.5 }}>{u.include}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700 }}>{nf.format(u.count)}</TableCell>
                </TableRow>
              );
              return unused
                ? <Tooltip key={u.include} title="sin uso: candidato a sacar del SPF" placement="left">{row}</Tooltip>
                : row;
            })}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
}

// ---------- Tab: DNS ----------
function DnsTab({ dns, onCopy }: { dns: Dns; onCopy: (t: string) => void }) {
  const spf = dns.spf, dm = dns.dmarc, soa = dns.soa;
  const lookups = spf?.lookups ?? 0;
  const lookupColor = lookups >= 10 ? "error" : lookups >= 8 ? "warning" : "success";
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="stretch">
        <SectionCard title="SPF" sx={{ minWidth: 340 }}>
          <RecordBox value={spf?.record} onCopy={onCopy} emptyText="sin SPF" />
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>DNS lookups {lookups}/10</Typography>
          <LinearProgress variant="determinate" value={Math.min(100, lookups * 10)} color={lookupColor} sx={{ height: 8, borderRadius: 4, mb: 1 }} />
          <KV k="all" v={spf?.all ? <code>{spf.all}</code> : "—"} />
          <KV k="Rangos (CIDR) que autoriza" v={nf.format(spf?.cidr_count ?? 0)} />
          <KV k="Incluye Google (_spf.google.com)" v={<YesNo v={spf?.has_google} />} />
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, display: "block", mt: 1 }}>includes</Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {(spf?.includes ?? []).length === 0 && <Typography variant="caption" sx={{ color: "text.secondary" }}>ninguno</Typography>}
            {(spf?.includes ?? []).map((i) => <Chip key={i} size="small" label={i} sx={{ fontFamily: "monospace", fontSize: 11 }} />)}
          </Stack>
          {(spf?.errors ?? []).length > 0 && (
            <Alert severity="error" sx={{ mt: 1, py: 0 }}>{spf.errors.join(" · ")}</Alert>
          )}
        </SectionCard>

        <SectionCard title="DMARC" sx={{ minWidth: 340 }}>
          <RecordBox value={dm?.record} onCopy={onCopy} emptyText="sin DMARC" />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <PolicyChip dmarc={dm} />
            {dm?.multiple && <Chip size="small" label="múltiples registros" sx={{ height: 22, color: "#B91C1C", bgcolor: "#FEE2E2", fontWeight: 700 }} />}
            <Tooltip title={dm?.rua_ok ? "El rua apunta a dmarc@cucuru.biz: este monitor recibe sus reportes." : "El rua NO apunta a dmarc@cucuru.biz: este monitor no recibe sus reportes."}>
              <Chip size="small" label={dm?.rua_ok ? "rua → este monitor" : "rua no apunta acá"}
                sx={{ height: 22, fontWeight: 700, color: dm?.rua_ok ? "#166534" : "#9A3412", bgcolor: dm?.rua_ok ? "#DCFCE7" : "#FFEDD5" }} />
            </Tooltip>
          </Stack>
          <KV k="p (política)" v={dm?.p ?? "—"} mono />
          <KV k="sp (subdominios)" v={dm?.sp ?? "— (hereda p)"} mono />
          <KV k="pct" v={dm?.pct != null ? `${dm.pct} %` : "— (100)"} mono />
          <KV k="rua" v={(dm?.rua ?? []).join(", ") || "—"} mono />
          <KV k="ruf" v={(dm?.ruf ?? []).join(", ") || "—"} mono />
          <KV k="adkim / aspf" v={`${dm?.adkim ?? "r"} / ${dm?.aspf ?? "r"}`} mono />
          <KV k="fo" v={dm?.fo ?? "0"} mono />
          {(dm?.rua_authorized ?? []).length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>Autorización del rua externo (_report._dmarc)</Typography>
              {dm.rua_authorized.map((a) => (
                <KV key={a.rua_domain} k={a.rua_domain} v={<><YesNo v={a.authorized} /> {a.authorized ? "autorizado" : "NO autorizado"}</>} />
              ))}
            </Box>
          )}
        </SectionCard>
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="stretch">
        <SectionCard title={`MX (${dns.mx?.length ?? 0})`} sx={{ minWidth: 340 }}>
          {(dns.mx ?? []).length === 0
            ? <Typography variant="body2" sx={{ color: "text.secondary" }}>Sin MX: el dominio no recibe mail.</Typography>
            : (
              <Table size="small">
                <TableHead><TableRow><TableCell>Prio</TableCell><TableCell>Host</TableCell><TableCell>IP</TableCell><TableCell>PTR</TableCell></TableRow></TableHead>
                <TableBody>
                  {dns.mx.map((m, i) => (
                    <TableRow key={`${m.host}-${i}`}>
                      <TableCell sx={{ fontSize: 12 }}>{m.priority}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }}>
                        {m.host}
                        {m.is_google && <Chip size="small" label="Google" sx={{ ml: 0.5, height: 18, fontSize: 10 }} />}
                        {m.is_null && <Chip size="small" label="null MX" sx={{ ml: 0.5, height: 18, fontSize: 10 }} />}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }}>{m.ip ?? "—"}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontFamily: "monospace" }}>{m.ptr ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </SectionCard>

        <SectionCard title="DKIM (selectores)">
          {(dns.dkim ?? []).length === 0 && <Typography variant="body2" sx={{ color: "text.secondary" }}>No se detectó ningún selector conocido.</Typography>}
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {(dns.dkim ?? []).map((d) => (
              <Tooltip key={d.selector} title={`${d.present ? "publicado" : "no publicado"}${d.cname ? ` · CNAME → ${d.cname}` : ""}${d.seen_in_reports ? " · visto en reportes" : ""}`}>
                <Chip size="small" label={`${d.selector}._domainkey`} variant={d.present ? "filled" : "outlined"}
                  sx={{ fontFamily: "monospace", fontSize: 11, color: d.present ? "#166534" : "#6B7280", bgcolor: d.present ? "#DCFCE7" : undefined }} />
              </Tooltip>
            ))}
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>BIMI</Typography>
          <RecordBox value={dns.bimi?.record} onCopy={onCopy} emptyText="sin BIMI (requiere p=quarantine/reject y logo VMC)" />
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Web</Typography>
          <KV k="A" v={(dns.web?.a ?? []).join(", ") || "—"} mono />
          <KV k="www CNAME" v={dns.web?.www_cname ?? "—"} mono />
        </SectionCard>

        <SectionCard title="NS / SOA">
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>NS</Typography>
          <Box sx={{ mb: 1 }}>
            {(dns.ns ?? []).map((n) => <Typography key={n} sx={{ fontFamily: "monospace", fontSize: 12.5 }}>{n}</Typography>)}
          </Box>
          <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>SOA</Typography>
          {soa?.present ? (
            <>
              <KV k="mname" v={soa.mname ?? "—"} mono />
              <KV k="rname" v={soa.rname ?? "—"} mono />
              <KV k="serial" v={<>{soa.serial ?? "—"} {soa.serial_format_ok ? <YesNo v /> : <Tooltip title="El serial no sigue el formato YYYYMMDDnn"><span><YesNo v={false} /></span></Tooltip>}</>} mono />
              <KV k="refresh / retry" v={`${soa.refresh ?? "—"} / ${soa.retry ?? "—"}`} mono />
              <KV k="expire" v={<>{soa.expire ?? "—"} {soa.expire_ok ? <YesNo v /> : <Tooltip title="expire fuera del rango recomendado (RFC 1912: 2–4 semanas)"><span><YesNo v={false} /></span></Tooltip>}</>} mono />
              <KV k="minimum (neg. TTL)" v={soa.minimum ?? "—"} mono />
            </>
          ) : <Typography variant="body2" sx={{ color: "text.secondary" }}>Sin SOA.</Typography>}
        </SectionCard>
      </Stack>
    </Stack>
  );
}

// ---------- Tab: Histórico ----------
function HistoricoTab({ hist }: { hist: HistoricoPoint[] }) {
  const x = hist.map((h) => h.date);
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Evolución diaria (ventana móvil de 30 días)</Typography>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Cada punto es la evaluación de ese día: % alineado (izquierda) y mails en la ventana (derecha).
      </Typography>
      <LineChart
        height={320}
        xAxis={[{ data: x, scaleType: "point", valueFormatter: (d: string) => fmtDay(d).slice(0, 5) }]}
        yAxis={[{ id: "pct", min: 0, max: 100, label: "% alineado" }, { id: "total", label: "mails" }]}
        rightAxis="total"
        series={[
          { id: "pct", yAxisId: "pct", label: "% alineado", data: hist.map((h) => h.aligned_pct), color: BRAND.primary, showMark: true, connectNulls: true,
            valueFormatter: (v: number | null) => fmtPct(v) },
          { id: "total", yAxisId: "total", label: "mails (30 d)", data: hist.map((h) => h.total), color: "#0FA958", showMark: false, area: true },
        ]}
        slotProps={{ legend: { direction: "row", position: { vertical: "top", horizontal: "middle" } } }}
        sx={{ ".MuiAreaElement-root": { opacity: 0.12 } }}
      />
      <TableContainer sx={{ maxHeight: 260 }}>
        <Table size="small" stickyHeader>
          <TableHead><TableRow>
            <TableCell>Día</TableCell><TableCell align="right">Mails</TableCell><TableCell align="right">% alineado</TableCell>
            <TableCell align="right">No autorizados</TableCell><TableCell align="right">Días con datos</TableCell><TableCell>Abiertos</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {[...hist].reverse().map((h) => (
              <TableRow key={h.date} hover>
                <TableCell sx={{ fontSize: 12 }}>{fmtDay(h.date)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700 }}>{nf.format(h.total)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 12 }}>{fmtPct(h.aligned_pct)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 12, color: h.unauthorized ? "#B91C1C" : undefined }}>{nf.format(h.unauthorized)}</TableCell>
                <TableCell align="right" sx={{ fontSize: 12 }}>{h.days_with_data}</TableCell>
                <TableCell><OpenBySeverityChips o={h.open_by_severity} emptyText="—" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

// ============================================================================
// Página
// ============================================================================
type TabKey = "resumen" | "hallazgos" | "fuentes" | "dns" | "historico";

export default function MonitoreoDominio() {
  const [summaries, setSummaries] = useState<DominioSummary[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [status, setStatus] = useState<DominioStatus | null>(null);
  const [hist, setHist] = useState<HistoricoPoint[]>([]);
  const [tab, setTab] = useState<TabKey>("resumen");
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [reevaluando, setReevaluando] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const refreshTimer = useRef<number | null>(null);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const rows = await listDominios();
      setSummaries(rows);
      setErr(null);
      return rows;
    } catch (e: any) { setErr(String(e?.message ?? e)); return null; }
    finally { setLoadingList(false); }
  }, []);

  const loadDetail = useCallback(async (domain: string) => {
    setLoadingDetail(true);
    try {
      const [st, h] = await Promise.all([
        getDominio(domain),
        getHistorico(domain, 30).catch(() => [] as HistoricoPoint[]),
      ]);
      setStatus(st); setHist(h); setErr(null);
    } catch (e: any) {
      setStatus(null); setHist([]);
      // 404 con missing → el evaluator no lo escribió aún; se muestra como estado vacío, no error.
      if (!/todavía no fue evaluado/i.test(String(e?.message ?? ""))) setErr(String(e?.message ?? e));
    } finally { setLoadingDetail(false); }
  }, []);

  // Carga inicial: lista + selección del primer dominio evaluado.
  useEffect(() => {
    (async () => {
      const rows = await loadList();
      if (!rows) return;
      const first = rows.find((r) => !r.missing) ?? rows[0];
      if (first) setSelected(first.domain);
    })();
    return () => { if (refreshTimer.current) window.clearTimeout(refreshTimer.current); };
  }, [loadList]);

  useEffect(() => { if (selected) loadDetail(selected); }, [selected, loadDetail]);

  const refreshAll = useCallback(async () => {
    await loadList();
    if (selected) await loadDetail(selected);
  }, [loadList, loadDetail, selected]);

  const onReevaluar = async () => {
    if (!selected) return;
    setReevaluando(true);
    try {
      await reevaluar(selected);
      setSnack(`Evaluación de ${selected} en curso… los datos se actualizan en ~${REEVAL_REFRESH_MS / 1000} s.`);
      if (refreshTimer.current) window.clearTimeout(refreshTimer.current);
      refreshTimer.current = window.setTimeout(async () => {
        await refreshAll();
        setSnack(`${selected}: datos actualizados.`);
        setReevaluando(false);
      }, REEVAL_REFRESH_MS);
    } catch (e: any) {
      setErr(String(e?.message ?? e));
      setReevaluando(false);
    }
  };

  const onCopy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); setSnack("Copiado al portapapeles."); }
    catch { setErr("No se pudo copiar al portapapeles."); }
  };

  const lastEval = useMemo(() => {
    const ts = (summaries ?? []).map((s) => s.evaluated_at).filter(Boolean) as string[];
    return ts.length ? ts.sort().at(-1)! : null;
  }, [summaries]);

  const selectedSummary = summaries?.find((s) => s.domain === selected);
  const showHistorico = hist.length > 1;
  const effectiveTab: TabKey = tab === "historico" && !showHistorico ? "resumen" : tab;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Dominios: DMARC / SPF / DNS</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Qué dice el DNS de cada dominio cucuru y quién manda mail en su nombre, según los reportes DMARC que
            recibe el monitor (<code>dmarc@cucuru.biz</code>). Esta página sólo muestra información: no manda mails,
            no crea alertas ni modifica DNS.
            {lastEval && <> Última evaluación: <b>{fmtArDateTime(lastEval)}</b> (hora Argentina).</>}
          </Typography>
        </Box>
        <Tooltip title="Volver a leer">
          <span>
            <IconButton onClick={refreshAll} disabled={loadingList || loadingDetail}>
              {loadingList ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}

      {/* Tarjetas */}
      {summaries === null && !err && <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>}
      {summaries && (
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
          {summaries.map((s) => (
            <DomainCard key={s.domain} s={s} selected={s.domain === selected} onClick={() => { setSelected(s.domain); setTab("resumen"); }} />
          ))}
        </Stack>
      )}

      {/* Detalle */}
      {selected && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{selected}</Typography>
            {status && <PolicyChip dmarc={status.dns?.dmarc} size="medium" />}
            {status?.ticket && <Chip size="small" label={status.ticket} variant="outlined" sx={{ fontWeight: 700 }} />}
            {status && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Evaluado {fmtWhen(status.evaluated_at)} · {MANAGED_IN_LABEL[status.managed_in] ?? status.managed_in}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            {loadingDetail && <CircularProgress size={18} />}
            <Tooltip title={`Invoca el evaluator del monitor para ${selected} y vuelve a leer a los ${REEVAL_REFRESH_MS / 1000} s. No cambia nada en DNS.`}>
              <span>
                <Button variant="contained" size="small" startIcon={reevaluando ? <CircularProgress size={16} color="inherit" /> : <AutorenewIcon />}
                  onClick={onReevaluar} disabled={reevaluando}>
                  {reevaluando ? "Reevaluando…" : "Reevaluar ahora"}
                </Button>
              </span>
            </Tooltip>
          </Stack>

          {/* Estados vacíos explícitos */}
          {!status && !loadingDetail && (
            <Alert severity="info">
              {selectedSummary?.missing
                ? <>El monitor todavía no evaluó <b>{selected}</b>. Tocá “Reevaluar ahora” para generar el primer estado.</>
                : <>Sin datos para <b>{selected}</b>.</>}
            </Alert>
          )}
          {status && status.dns?.dmarc?.rua_ok === false && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              El DMARC de este dominio no apunta a <code>dmarc@cucuru.biz</code>: este monitor no va a recibir sus reportes.
            </Alert>
          )}
          {status && (status.reports?.count_total ?? 0) === 0 && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              Todavía no llegaron reportes. Los receptores mandan uno por día; el primero tarda 24-48 h desde que se publica el <code>rua</code>.
            </Alert>
          )}

          {status && (
            <>
              <Tabs value={effectiveTab} onChange={(_e, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ mb: 2, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                <Tab value="resumen" label="Resumen" />
                <Tab value="hallazgos" label={`Hallazgos (${openCount(status.open_by_severity)})`} />
                <Tab value="fuentes" label={`Fuentes (${status.traffic?.sources?.length ?? 0})`} />
                <Tab value="dns" label="DNS" />
                {showHistorico && <Tab value="historico" label={`Histórico (${hist.length} d)`} />}
              </Tabs>
              {effectiveTab === "resumen"   && <ResumenTab st={status} />}
              {effectiveTab === "hallazgos" && <HallazgosTab findings={status.findings ?? []} />}
              {effectiveTab === "fuentes"   && <FuentesTab traffic={status.traffic} />}
              {effectiveTab === "dns"       && <DnsTab dns={status.dns} onCopy={onCopy} />}
              {effectiveTab === "historico" && showHistorico && <HistoricoTab hist={hist} />}
            </>
          )}
        </Paper>
      )}

      <Snackbar open={!!snack} autoHideDuration={6000} onClose={() => setSnack(null)} message={snack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </Box>
  );
}
